import { useEffect, useState } from 'react';
import { getMirroredDeclaration } from 'store/declarations/client';
import { Declaration } from 'store/declarations/declaration';
import { MIRRORED_DECLARATIONS_PAGE_SIZE } from './constants';
import { getLinkedMirroredDeclarations } from './getLinkedMirroredDeclarations';
import { sortMirroredDeclarations } from './sortMirroredDeclarations';

/**
 * The declarations linked to this one: the B1s of an H1, or the H1 of a B1 (the sibling B1s the BE
 * also lists are left out).
 *
 * Keyed on `mirrorDeclarationIdLink` rather than `mirrored`, because only the B1 is flagged as
 * mirrored - the H1 just carries the link. It is cleared on unlink, so no request is made then.
 */
const useMirroredDeclarations = (declaration?: Declaration) => {
    const declarationId = declaration?.id;
    const hasMirrorLink = Boolean(declaration?.mirrorDeclarationIdLink);

    const [mirroredDeclarations, setMirroredDeclarations] = useState<Declaration[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!declarationId || !hasMirrorLink) {
            setMirroredDeclarations([]);
            setIsLoading(false);
            return;
        }

        let isMounted = true;
        setIsLoading(true);

        getMirroredDeclaration(declarationId, { size: MIRRORED_DECLARATIONS_PAGE_SIZE })
            .then(({ list }) => {
                if (isMounted) setMirroredDeclarations(sortMirroredDeclarations(list));
            })
            .catch((error) => {
                // The links are secondary to the page, so a failure only hides them.
                console.error('Failed to fetch mirrored declarations', error);
                if (isMounted) setMirroredDeclarations([]);
            })
            .finally(() => {
                if (isMounted) setIsLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, [declarationId, hasMirrorLink]);

    return { mirroredDeclarations: getLinkedMirroredDeclarations(declaration, mirroredDeclarations), isLoading };
};

export default useMirroredDeclarations;
